<script lang="ts">
  // Family detail shown when a card is opened. Loads the full detail (description, tags, owner) and
  // offers the download/load action — which behaves differently by environment:
  //   • inside Revit: DownloadAndLoad, which fetches the .rfa and loads it into the open project;
  //   • in the browser: a plain download of the .rfa file.
  // The button is hidden entirely when the user is not signed in, since download requires a token.

  import { onMount } from "svelte";
  import { transport } from "@/transport";
  import { auth } from "@/stores/auth.svelte";
  import { catalog } from "./catalogStore.svelte";
  import { categoryLabel, languageLabel } from "./categories";
  import { findLoadedFamily, isTypeLoaded } from "./projectState.svelte";
  import { pickAssetYear } from "./versionMatrix";
  import FamilyViewer3D from "./FamilyViewer3D.svelte";
  import PublishUpdateDialog from "./PublishUpdateDialog.svelte";
  import type { FamilyDetail, FavoriteResult, LoadResult } from "./types";
  import { t, locale } from "@/i18n/index.svelte";
  import type { SupportedLocale } from "@/i18n/index.svelte";
  import {
    AppBadge,
    AppButton,
    AppDialog,
    AppSelect,
    AppState,
  } from "@/components/ui";

  let {
    slug,
    onclose,
    onfavoritechanged,
    ondeleted,
  }: {
    slug: string;
    onclose?: () => void;
    onfavoritechanged?: (slug: string, isFavorite: boolean) => void;
    ondeleted?: (slug: string) => void;
  } = $props();

  let detail = $state<FamilyDetail | null>(null);
  let status = $state<"loading" | "ready" | "error">("loading");
  let errorMessage = $state("");

  let action = $state<"idle" | "working" | "done" | "failed">("idle");
  let actionMessage = $state("");

  // Bookmarking is optimistic here for the same reason it is in the grid: the heart is feedback, and
  // feedback that arrives after a round-trip reads as a dead control. A refusal puts it back.
  let favoriteBusy = $state(false);

  async function toggleFavorite() {
    if (!detail || favoriteBusy) return;

    const next = !detail.isFavorite;
    detail.isFavorite = next;
    favoriteBusy = true;

    try {
      await transport.invoke<FavoriteResult>("SetFavorite", {
        slug,
        favorite: next,
      });
      onfavoritechanged?.(slug, next);
    } catch {
      if (detail) detail.isFavorite = !next;
    } finally {
      favoriteBusy = false;
    }
  }

  /** Narrows the catalog to this family's OmniClass branch, and gets out of the way to show it. */
  function filterByOmniClass() {
    if (!detail?.omniClassCode) return;
    catalog.setOmniClass(detail.omniClassCode, detail.omniClassTitle);
    onclose?.();
  }

  const category = $derived(
    detail
      ? categoryLabel(detail.revitCategory, locale.value as SupportedLocale)
      : "",
  );

  // Which asset year this Revit would receive (Revit only). Null → the host is too old for any
  // available asset, so the load button is disabled with an explanation.
  const deliveredYear = $derived.by(() => {
    const host = transport.caps.revitYear;
    if (host == null || !detail) return null;
    return pickAssetYear(detail.availableYears, host);
  });

  const incompatible = $derived(
    transport.caps.revitYear != null && deliveredYear === null,
  );

  // A parameter's identity kind, colour-coded: built-in and shared parameters keep a stable identity
  // across languages (green / accent); a family-local parameter is matched only by its raw name (muted).
  function kindClass(kind: string): string {
    if (kind === "builtin") return "text-success";
    if (kind === "shared") return "text-accent";
    return "text-ink-muted";
  }

  onMount(() => {
    transport
      .invoke<FamilyDetail>("GetFamilyDetail", { slug })
      .then((d) => {
        detail = d;
        // Pre-select the first type so the picker never opens on a blank row, and so placing without
        // touching it means "the family's first type" rather than "no type".
        selectedType = d.types[0] ?? "";
        loadTypeName = d.types[0] ?? "";
        status = "ready";
      })
      .catch((error) => {
        errorMessage = error instanceof Error ? error.message : String(error);
        status = "error";
      });
  });

  // Which type (size) to place. Only asked for when the family ships more than one — a picker over a
  // single option is a question with one answer.
  let selectedType = $state<string>("");

  const needsTypeChoice = $derived(
    transport.caps.revit && (detail?.types.length ?? 0) > 1,
  );

  // Whether this family, or one of its types, is already in the active document — from the snapshot
  // GetLoadedFamilies took when the grid mounted (catalogStore.loadProjectState), cross-referenced
  // here rather than asked of Revit again.
  const projectMatch = $derived(
    transport.caps.revit && detail
      ? findLoadedFamily(catalog.state.loadedFamilies, detail.name)
      : undefined,
  );
  const isInProject = $derived(projectMatch !== undefined);

  function typeIsLoadedInProject(ty: string): boolean {
    return (
      !!detail && isTypeLoaded(catalog.state.loadedFamilies, detail.name, ty)
    );
  }

  function formatDateTime(iso: string | null | undefined): string {
    if (!iso) return "—";
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleString();
  }

  /**
   * Downloads, loads, and hands the user Revit's placement tool. The distinction from loading is the
   * one that matters while modelling: loading leaves the family somewhere in the project browser to
   * be found, placing puts them straight into drawing it.
   *
   * Placing always loads the whole family — the type list here is only about which one is placed, not
   * which ones reach the project. The "load one type only" choice below is for the "load into project"
   * action, where somebody is stocking ahead rather than drawing right now.
   */
  async function placeInProject() {
    await runAction("DownloadAndPlace", {
      typeName: selectedType || detail?.types[0] || null,
    });
  }

  // Load mode for "load into project" (not placing): the whole family, or LoadFamilySymbol for just
  // one named type. Only worth asking about when there is more than one type to choose between.
  let loadMode = $state<"all" | "one">("all");
  let loadTypeName = $state<string>("");
  const needsLoadModeChoice = $derived(
    transport.caps.revit && (detail?.types.length ?? 0) > 1,
  );

  /** Loads without placing — for stocking a project up front rather than modelling right now. */
  async function loadIntoProject() {
    await runAction("DownloadAndLoad", {
      typeName:
        needsLoadModeChoice && loadMode === "one"
          ? loadTypeName || detail?.types[0] || null
          : null,
    });
  }

  async function runAction(command: string, extra: Record<string, unknown>) {
    if (!detail) return;
    action = "working";
    actionMessage = "";

    try {
      const result = await transport.invoke<LoadResult>(command, {
        slug: detail.slug,
        ...extra,
      });

      if (!result.loaded) {
        action = "failed";
        actionMessage =
          result.reason ??
          t("detail.notAvailableForYear", {
            years: result.availableYears.join(", "),
          });
        return;
      }

      action = "done";

      // Three different true things can have happened, and saying the wrong one is worse than saying
      // nothing: Revit is now waiting for a click, or the family is merely in the project, or it was
      // already there and nothing moved.
      if (result.placing) {
        actionMessage = result.placedTypeName
          ? t("detail.placingType", { type: result.placedTypeName })
          : t("detail.placing");
      } else if (result.reason) {
        // Loaded, but placement could not start — an annotation family in a 3D view, say. The reason
        // is the useful half of that answer.
        actionMessage = result.reason;
      } else if (result.alreadyLoaded) {
        actionMessage = t("detail.alreadyLoaded");
      } else {
        actionMessage = t("detail.loadedFromYear", { year: result.assetYear });
      }
    } catch (error) {
      action = "failed";
      actionMessage = error instanceof Error ? error.message : String(error);
    }
  }

  // Deletion is permanent — everything this family owns goes with it — so it asks twice, the same
  // shape as the moderation queue's reject confirmation: a click reveals the confirm/cancel pair
  // rather than acting on the first click alone.
  let confirmingDelete = $state(false);
  let deleteBusy = $state(false);
  let deleteError = $state("");

  // Publishing an update is offered only to the owner (canDelete's gate — see its own comment below)
  // of an already-approved family, and only inside Revit: the update flow needs PickFiles and
  // ExtractFamilyPackage, neither of which exist in the browser.
  let showUpdateDialog = $state(false);
  const canPublishUpdate = $derived(
    detail?.canDelete && transport.caps.revit && detail.status === "approved",
  );

  async function confirmDelete() {
    if (!detail) return;
    deleteBusy = true;
    deleteError = "";
    try {
      const result = await transport.invoke<{
        ok: boolean;
        error?: string | null;
      }>("DeleteFamily", {
        slug: detail.slug,
      });
      if (result.ok) ondeleted?.(detail.slug);
      else deleteError = result.error ?? t("detail.deleteFailed");
    } catch (error) {
      deleteError = error instanceof Error ? error.message : String(error);
    } finally {
      deleteBusy = false;
    }
  }
</script>

<AppDialog
  size="sm"
  floatActions
  closeLabel={t("publish.close")}
  onclose={() => onclose?.()}
>
  {#snippet media()}
    {#if detail}
      <div class="aspect-video bg-panel flex items-center justify-center">
        <!-- A published family has a static, pre-built 3D mesh in object storage; the viewer
             fetches it directly and works the same in the browser and in Revit. Falls back to the
             thumbnail, then a placeholder. -->
        {#if detail.meshUrl}
          <FamilyViewer3D
            meshUrl={detail.meshUrl}
            cacheKey={detail.slug}
            class="w-full h-full"
          />
        {:else if detail.thumbnailUrl}
          <img
            src={detail.thumbnailUrl}
            alt={detail.name}
            class="max-w-full max-h-full object-contain"
          />
        {:else}
          <span class="text-ink-muted text-sm">{t("detail.noPreview")}</span>
        {/if}
      </div>
    {/if}
  {/snippet}

  {#snippet header()}
    {#if detail}
      <div class="flex items-center gap-2 min-w-0">
        <h2 class="text-lg font-semibold truncate">{detail.name}</h2>
        {#if isInProject}
          <AppBadge
            tone="success"
            title={t("catalog.badgeInProjectHint")}
            class="shrink-0"
          >
            {t("catalog.badgeInProject")}
          </AppBadge>
        {/if}
      </div>
      <p class="text-sm text-ink-muted truncate">
        {category} · {detail.ownerName}
      </p>
    {/if}
  {/snippet}

  {#snippet actions()}
    {#if detail}
      {#if auth.state.status === "signed-in"}
        <!-- Pinned in the panel's top-right corner alongside the close button (AppDialog's
             floatActions), not over the 3D preview itself — the preview now scrolls with the rest
             of the card instead of being reserved permanent space above it. -->
        <button
          class="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-white/90
                 backdrop-blur-sm transition-colors duration-200 ease-out disabled:opacity-50
                 {detail.isFavorite
            ? 'text-error'
            : 'text-ink-secondary'} hover:bg-white"
          aria-label={detail.isFavorite
            ? t("catalog.unfavorite")
            : t("catalog.favorite")}
          aria-pressed={detail.isFavorite}
          title={detail.isFavorite
            ? t("catalog.unfavorite")
            : t("catalog.favorite")}
          disabled={favoriteBusy}
          onclick={toggleFavorite}
        >
          <svg
            viewBox="0 0 24 24"
            class="w-5 h-5"
            fill={detail.isFavorite ? "currentColor" : "none"}
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path
              d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.7 1.1-1a5.5 5.5 0 0 0 0-7.7z"
            />
          </svg>
        </button>
      {/if}

      <!-- Publish a new version. Same canDelete gate as the delete button below — server-computed
           owner-or-super-admin — plus Revit-only and approved-only, since neither a pending
           submission nor an organisation's still-under-review one has anything live to update. -->
      {#if canPublishUpdate}
        <button
          class="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-white/90
                 backdrop-blur-sm text-ink-secondary transition-colors duration-200 ease-out hover:text-accent hover:bg-white"
          aria-label={t("update.open")}
          title={t("update.open")}
          onclick={() => (showUpdateDialog = true)}
        >
          <svg
            viewBox="0 0 24 24"
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path d="M21 12a9 9 0 1 1-2.6-6.36" />
            <path d="M21 3v6h-6" />
          </svg>
        </button>
      {/if}

      <!-- Permanent, so it only starts the confirm step here — the actual delete still asks a
           second time below. Only ever shown to the owner or a super admin; the server computes
           canDelete, this is not a client-side name comparison. -->
      {#if detail.canDelete}
        <button
          class="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-white/90
                 backdrop-blur-sm text-ink-secondary transition-colors duration-200 ease-out hover:text-error hover:bg-white"
          aria-label={t("detail.delete")}
          title={t("detail.delete")}
          onclick={() => (confirmingDelete = true)}
        >
          <svg
            viewBox="0 0 24 24"
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path d="M3 6h18" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <path d="M10 11v6M14 11v6" />
          </svg>
        </button>
      {/if}
    {/if}
  {/snippet}

  {#if status === "loading"}
    <AppState kind="loading">{t("common.loading")}</AppState>
  {:else if status === "error"}
    <AppState kind="error">{errorMessage}</AppState>
  {:else if detail}
    <!-- Owner-only: why the family itself was rejected (a brand-new submission that never made it
         live at all — an already-approved family never carries this, see PendingUpdate below for
         that case instead). -->
    {#if detail.status === "rejected" && detail.rejectionReason}
      <div class="rounded-surface border border-error/30 bg-error/5 p-3">
        <p class="text-xs font-medium text-error">{t("detail.rejected")}</p>
        <p class="text-sm text-ink mt-0.5">{detail.rejectionReason}</p>
      </div>
    {/if}

    <!-- Owner-only: an update sitting on its own version, invisible everywhere else in the
         storefront because the family itself keeps serving whatever was live before it. -->
    {#if detail.pendingUpdate}
      <div
        class="rounded-surface border p-3 {detail.pendingUpdate.status ===
        'rejected'
          ? 'border-error/30 bg-error/5'
          : 'border-line bg-panel'}"
      >
        <p
          class="text-xs font-medium {detail.pendingUpdate.status === 'rejected'
            ? 'text-error'
            : 'text-ink-muted'}"
        >
          {detail.pendingUpdate.status === "rejected"
            ? t("detail.updateRejected", {
                semver: detail.pendingUpdate.semver,
              })
            : t("detail.updatePending", {
                semver: detail.pendingUpdate.semver,
              })}
        </p>
        {#if detail.pendingUpdate.rejectionReason}
          <p class="text-sm text-ink mt-0.5">
            {detail.pendingUpdate.rejectionReason}
          </p>
        {/if}
      </div>
    {/if}

    {#if detail.description}
      <p class="text-sm text-ink-secondary">{detail.description}</p>
    {/if}

    <!-- Tags: what a searcher types to find this family, so they are shown here too. -->
    {#if detail.tags.length}
      <div>
        <p class="text-xs font-medium text-ink-muted mb-1">
          {t("detail.tags")}
        </p>
        <div class="flex flex-wrap gap-1">
          {#each detail.tags as tag (tag)}
            <span
              class="text-xs bg-panel border border-line rounded-full px-2 py-0.5"
              >{tag}</span
            >
          {/each}
        </div>
      </div>
    {/if}

    <dl class="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm">
      <dt class="text-ink-muted">{t("detail.language")}</dt>
      <dd>{languageLabel(detail.authoringLanguage)}</dd>
      {#if detail.omniClassCode}
        <dt class="text-ink-muted">{t("detail.omniClass")}</dt>
        <dd class="text-ink">
          <!-- Clicking the code is the only way into the OmniClass axis, and the natural one: on
               a family you are already looking at, a classification reads as "show me more like
               this". It closes the dialog because the answer is behind it. -->
          <button
            type="button"
            class="text-left rounded hover:text-accent transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            title={t("detail.omniClassFilter")}
            onclick={filterByOmniClass}
          >
            <span
              class="font-mono underline decoration-dotted underline-offset-2"
              >{detail.omniClassCode}</span
            >
            {#if detail.omniClassTitle}
              <span class="text-ink-muted"> · {detail.omniClassTitle}</span>
            {/if}
          </button>
        </dd>
      {/if}
      <dt class="text-ink-muted">{t("detail.units")}</dt>
      <dd>{detail.unitSystem ?? "—"}</dd>
      <dt class="text-ink-muted" title={t("publish.lodHint")}>
        {t("publish.lod")}
      </dt>
      <dd class="tabular-nums">{detail.lod ? `LOD ${detail.lod}` : "—"}</dd>
      <dt class="text-ink-muted">{t("detail.revitYears")}</dt>
      <dd class="tabular-nums">
        {detail.availableYears.join(" · ")}
        {#if deliveredYear != null}
          <span class="text-ink-muted">
            — {t("detail.willLoad", { year: deliveredYear })}
          </span>
        {/if}
      </dd>
      <dt class="text-ink-muted">{t("detail.lastModified")}</dt>
      <dd>{formatDateTime(detail.lastModifiedAt)}</dd>
      <!-- Recorded on insertion, not on download — a second insertion from the plugin's local
           cache never reaches the API, so this is a better "is this actually used" signal than a
           download count would be. -->
      <dt class="text-ink-muted">{t("detail.usageCount")}</dt>
      <dd class="tabular-nums">{detail.usageCount}</dd>
    </dl>

    <!-- Types + parameters, in the authoring language. Revit does not translate parameter
         names on load, so showing them here is what lets the buyer see exactly what they get. -->
    {#if detail.types.length || detail.parameters.length}
      <div class="flex flex-col gap-3">
        {#if detail.types.length}
          <div>
            <p class="text-xs font-medium text-ink-muted mb-1">
              {t("detail.types")}
            </p>
            <div class="flex flex-wrap gap-1">
              <!-- A type already loaded into the active document is marked here too — the same
                   projectMatch snapshot the "in this project" badge above uses. -->
              {#each detail.types as ty (ty)}
                <span
                  class="text-xs border rounded px-2 py-0.5 {typeIsLoadedInProject(
                    ty,
                  )
                    ? 'bg-panel border-success text-success'
                    : 'bg-panel border-line'}"
                  title={typeIsLoadedInProject(ty)
                    ? t("detail.typeAlreadyLoaded")
                    : undefined}>{ty}</span
                >
              {/each}
            </div>
          </div>
        {/if}

        {#if detail.parameters.length}
          <div>
            <p class="text-xs font-medium text-ink-muted mb-1">
              {t("detail.parameters")} · {languageLabel(
                detail.authoringLanguage,
              )}
            </p>
            <div class="border border-line rounded overflow-x-auto">
              <table class="w-full text-sm">
                <thead class="bg-panel text-ink-muted text-xs">
                  <tr>
                    <th class="text-left font-medium px-2 py-1.5">
                      {t("detail.parameterName")}
                    </th>
                    <th class="text-left font-medium px-2 py-1.5">
                      {t("detail.parameterType")}
                    </th>
                    <th class="text-left font-medium px-2 py-1.5">
                      {t("detail.value")}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-line">
                  {#each detail.parameters as p, i (i)}
                    <tr>
                      <td class="px-2 py-1.5 align-top">{p.rawName}</td>
                      <td class="px-2 py-1.5 align-top">
                        <span class="text-[10px] uppercase {kindClass(p.kind)}"
                          >{p.kind}</span
                        >
                        {#if p.isInstance}
                          <span
                            class="text-[10px] uppercase text-ink-muted ml-1"
                            >{t("detail.instance")}</span
                          >
                        {/if}
                      </td>
                      <td class="px-2 py-1.5 align-top text-ink-muted">
                        {#if !p.isFormulaDriven && p.value != null && p.value !== ""}
                          {p.value}
                        {:else}
                          —
                        {/if}
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          </div>
        {/if}
      </div>
    {/if}

    <!-- Host too old for any available asset: say so instead of offering a load that fails. -->
    {#if incompatible}
      <p class="text-xs text-error text-center">
        {t("detail.notAvailableForYear", {
          years: detail.availableYears.join(", "),
        })}
      </p>
    {:else if auth.state.status === "signed-in"}
      <!-- Download/load/place, gated on sign-in. -->
      <div class="flex flex-col gap-2">
        <!-- Which size to place. Asked before the click rather than after, because the placement
             tool starts immediately and there is no moment afterwards to interrupt with a
             question. -->
        {#if needsTypeChoice}
          <label class="flex flex-col gap-1">
            <span class="text-xs font-medium text-ink-muted"
              >{t("detail.chooseType")}</span
            >
            <AppSelect bind:value={selectedType}>
              {#each detail.types as ty (ty)}
                <option value={ty}>{ty}</option>
              {/each}
            </AppSelect>
          </label>
        {/if}

        <!-- Inside Revit the primary action is placing, not loading: a family in the project
             browser is not yet a thing in the model, and hunting for it is the step people
             actually resent. Loading stays available for stocking a project up front. -->
        <AppButton
          variant="primary"
          size="lg"
          block
          disabled={action === "working"}
          onclick={() =>
            transport.caps.revit ? placeInProject() : loadIntoProject()}
        >
          {#if action === "working"}
            {t("detail.loading")}
          {:else if transport.caps.revit}
            {t("detail.placeInProject")}
          {:else}
            {t("detail.download")}
          {/if}
        </AppButton>

        {#if transport.caps.revit}
          <!-- Load mode: bring in every type, or just one via LoadFamilySymbol. Scoped to this
               button rather than the "place" one above — placing always loads the whole family,
               since its type choice is about what gets drawn, not what reaches the project. -->
          {#if needsLoadModeChoice}
            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-ink-muted"
                >{t("detail.loadMode")}</span
              >
              <div class="flex items-center gap-4 text-sm">
                <label class="flex items-center gap-1.5">
                  <input type="radio" value="all" bind:group={loadMode} />
                  {t("detail.loadModeAll")}
                </label>
                <label class="flex items-center gap-1.5">
                  <input type="radio" value="one" bind:group={loadMode} />
                  {t("detail.loadModeOne")}
                </label>
              </div>
              {#if loadMode === "one"}
                <AppSelect bind:value={loadTypeName}>
                  {#each detail.types as ty (ty)}
                    <option value={ty}>
                      {ty}{typeIsLoadedInProject(ty)
                        ? ` — ${t("detail.typeAlreadyLoaded")}`
                        : ""}
                    </option>
                  {/each}
                </AppSelect>
              {/if}
            </div>
          {/if}

          <AppButton
            variant="secondary"
            size="md"
            block
            disabled={action === "working"}
            onclick={loadIntoProject}
          >
            {t("detail.loadIntoProject")}
          </AppButton>
        {/if}
        {#if actionMessage}
          <p
            class="text-xs text-center {action === 'failed'
              ? 'text-error'
              : 'text-ink-muted'}"
          >
            {actionMessage}
          </p>
        {/if}
      </div>
    {:else}
      <p class="text-xs text-ink-muted text-center">
        {t("detail.signInToDownload")}
      </p>
    {/if}

    <!-- Triggered by the trash icon pinned next to the heart, above — this is only the second,
         confirming ask, the same shape as the moderation queue's reject confirmation. -->
    {#if detail.canDelete && confirmingDelete}
      <div class="border-t border-line pt-3">
        <div class="flex flex-col gap-2">
          <p class="text-xs text-error">{t("detail.confirmDelete")}</p>
          <div class="flex gap-2">
            <AppButton
              variant="danger"
              size="sm"
              disabled={deleteBusy}
              onclick={confirmDelete}
            >
              {deleteBusy
                ? t("moderation.working")
                : t("detail.confirmDeleteButton")}
            </AppButton>
            <AppButton
              variant="ghost"
              size="sm"
              disabled={deleteBusy}
              onclick={() => (confirmingDelete = false)}
            >
              {t("moderation.cancel")}
            </AppButton>
          </div>
        </div>
        {#if deleteError}
          <p class="text-xs text-error mt-1">{deleteError}</p>
        {/if}
      </div>
    {/if}
  {/if}
</AppDialog>

{#if showUpdateDialog && detail}
  <PublishUpdateDialog
    slug={detail.slug}
    onclose={() => (showUpdateDialog = false)}
  />
{/if}
